(() => {
  if (customElements.get('product-story')) return;
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const smooth = (a, b, v) => { const t = clamp((v - a) / (b - a)); return t * t * (3 - 2 * t); };
  const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const mix = (a, b, t) => a * (1 - t) + b * t;
  const base = { cx: 0.5, cy: 0.5, s: 0.9, px: 0, py: 0, rx: 2, ry: -2, rz: 0, fx: 0.5, fy: 0.5, fw: 1.5, fh: 1.5, fa: 0 };
  const hero = { ...base, s: 0.68, px: 0.02, py: 0.13, rx: 13, ry: -21, rz: -3.5 };
  const outro = { ...base, s: 0.8, py: -0.025, rx: 1, ry: 0 };

  function makeTimeline(chapters) {
    const timeline = [[0, hero], [0.085, base], [0.14, base]];
    const holds = [];
    const span = 0.72 / Math.max(1, chapters.length);
    chapters.forEach((chapter, i) => {
      const start = 0.2 + span * i;
      const end = start + span * 0.54;
      const x = Number(chapter.dataset.focusX) / 100;
      const y = Number(chapter.dataset.focusY) / 100;
      const pose = { ...base, cx: x, cy: y, s: Number(chapter.dataset.zoom) / 100, px: 0.23, py: 0.015, rx: 3 + (i % 2) * 2, ry: i % 2 ? -13 : 11, rz: i % 2 ? -0.6 : 0.6, fx: x, fy: y, fw: 0.85, fh: 0.9, fa: 0.55 };
      timeline.push([start, pose], [end, pose]);
      holds.push([start, end]);
    });
    timeline.push([0.95, outro], [1, outro]);
    return { timeline, holds };
  }

  function poseAt(timeline, progress) {
    const p = clamp(progress);
    const i = timeline.findIndex(([at]) => at >= p);
    if (i <= 0) return { ...timeline[0][1] };
    const [a, from] = timeline[i - 1];
    const [b, to] = timeline[i];
    const t = ease(clamp((p - a) / (b - a)));
    return Object.fromEntries(Object.keys(base).map(key => [key, mix(from[key], to[key], t)]));
  }

  function place(pose, width, height, vw, vh, d) {
    const DEG = Math.PI / 180;
    let x = (pose.cx - 0.5) * width * pose.s;
    let y = (pose.cy - 0.5) * height * pose.s;
    let z = 0;
    const a = pose.rz * DEG, b = pose.ry * DEG, c = pose.rx * DEG;
    [x, y] = [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
    [x, z] = [x * Math.cos(b) + z * Math.sin(b), -x * Math.sin(b) + z * Math.cos(b)];
    [y, z] = [y * Math.cos(c) - z * Math.sin(c), y * Math.sin(c) + z * Math.cos(c)];
    const k = (d - z) / d;
    return { tx: pose.px * vw * k - x, ty: pose.py * vh * k - y };
  }

  class ProductStory extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.film = this.querySelector('.ps-film');
      this.stage = this.querySelector('.ps-stage');
      this.camera = this.querySelector('.ps-camera');
      this.rig = this.querySelector('.ps-rig');
      this.face = this.querySelector('.ps-face');
      this.focus = this.querySelector('.ps-face__focus');
      this.chapters = [...this.querySelectorAll('.ps-chapter')];
      this.chapterFrames = [...this.querySelectorAll('[data-chapter-frame]')];
      this.toggle = this.querySelector('[data-motion-toggle]');
      const { timeline, holds } = makeTimeline(this.chapters);
      this.timeline = timeline;
      this.holds = holds;
      this.shown = this.target = 0;
      this.pointer = { x: 0, y: 0, sx: 0, sy: 0 };
      this.motion = matchMedia('(min-width: 1000px) and (min-height: 620px) and (prefers-reduced-motion: no-preference)');
      this.motion.addEventListener('change', () => this.setMode(), { signal });
      window.addEventListener('scroll', () => { this.target = this.progress(); this.wake(); }, { passive: true, signal });
      window.addEventListener('resize', () => { this.measure(); this.wake(); }, { signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.wake(); }, { signal });
      this.stage.addEventListener('pointermove', event => {
        if (event.pointerType !== 'mouse' || !this.filmMode) return;
        this.pointer.x = event.clientX / innerWidth - 0.5;
        this.pointer.y = event.clientY / innerHeight - 0.5;
        this.wake();
      }, { passive: true, signal });
      this.stage.addEventListener('pointerleave', () => { this.pointer.x = this.pointer.y = 0; this.wake(); }, { signal });
      this.toggle.addEventListener('click', () => {
        const relative = this.progress();
        this.staticRequested = !this.staticRequested;
        this.setMode();
        window.scrollTo({ top: this.start + relative * this.travel, behavior: 'instant' });
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        this.staticRequested = true;
        this.setMode();
        event.target.scrollIntoView({ block: 'center' });
      }, { signal });
      this.setMode();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.camera.style.removeProperty('--slab-r');
    }

    setMode() {
      this.filmMode = this.motion.matches && !this.staticRequested;
      this.classList.toggle('is-film', this.filmMode);
      this.classList.add('is-ready');
      this.toggle.textContent = this.filmMode ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel;
      this.toggle.setAttribute('aria-pressed', String(!this.filmMode));
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.camera.style.removeProperty('--slab-r');
      if (!this.filmMode) {
        for (const el of [this, this.rig, this.face, this.focus, ...this.chapters]) el.removeAttribute('data-active');
        this.chapters.forEach(el => el.classList.remove('is-active'));
      }
      this.measure();
      this.shown = this.target = this.progress();
      this.wake();
    }

    measure() {
      this.start = this.film.getBoundingClientRect().top + scrollY;
      this.travel = Math.max(1, this.film.offsetHeight - innerHeight);
      this.raster = Number.parseFloat(getComputedStyle(this.camera).getPropertyValue('--slab-r')) || 1;
      this.width = this.rig.offsetWidth / this.raster;
      this.height = this.rig.offsetHeight / this.raster;
      this.depth = this.width * 2.1;
    }

    progress() { return clamp((scrollY - this.start) / this.travel); }

    wake() {
      if (!this.filmMode || this.frame || document.hidden) return;
      this.last = performance.now();
      this.frame = requestAnimationFrame(now => this.step(now));
    }

    step(now) {
      this.frame = 0;
      if (!this.filmMode || document.hidden) return;
      const k = 1 - Math.exp(-Math.min(0.05, (now - this.last) / 1000) * 9);
      this.last = now;
      this.shown += (this.target - this.shown) * k;
      if (Math.abs(this.target - this.shown) < 0.00005) this.shown = this.target;
      this.pointer.sx += (this.pointer.x - this.pointer.sx) * k;
      this.pointer.sy += (this.pointer.y - this.pointer.sy) * k;
      this.apply(this.shown);
      if (this.shown !== this.target || Math.abs(this.pointer.x - this.pointer.sx) > 0.0005 || Math.abs(this.pointer.y - this.pointer.sy) > 0.0005) this.frame = requestAnimationFrame(t => this.step(t));
    }

    apply(p) {
      const pose = poseAt(this.timeline, p);
      const raster = pose.s > 1.05 ? Math.max(1, Math.min(2, 2400 / this.width)) : pose.s < 0.98 ? 1 : this.raster;
      if (Math.abs(raster - this.raster) > 0.001) {
        this.camera.style.setProperty('--slab-r', raster);
        this.measure();
      }
      pose.ry += this.pointer.sx * 2.4;
      pose.rx -= this.pointer.sy * 1.6;
      const { tx, ty } = place(pose, this.width, this.height, innerWidth, innerHeight, this.depth);
      this.rig.style.setProperty('--rig', `translate3d(${tx}px, ${ty}px, 0) rotateX(${pose.rx}deg) rotateY(${pose.ry}deg) rotateZ(${pose.rz}deg) scale(${pose.s / this.raster})`);
      for (const key of ['fx', 'fy', 'fw', 'fh']) this.focus.style.setProperty(`--${key}`, `${pose[key] * 100}%`);
      this.focus.style.setProperty('--fa', pose.fa);
      this.face.style.setProperty('--sheen', `${50 - pose.ry * 1.6}%`);
      this.style.setProperty('--open', 1 - smooth(0.012, 0.07, p));
      this.style.setProperty('--intro', 1 - smooth(0, 0.045, p));
      this.style.setProperty('--intro-vis', p < 0.045 ? 'visible' : 'hidden');
      this.style.setProperty('--coda', smooth(0.915, 0.95, p));
      this.chapters.forEach((chapter, i) => {
        const [start, end] = this.holds[i];
        const amount = smooth(start - 0.035, start + 0.004, p) * (1 - smooth(end - 0.004, end + 0.035, p));
        chapter.style.setProperty('--in', amount);
        chapter.classList.toggle('is-active', amount > 0.01);
        const frame = this.chapterFrames.find(image => Number(image.dataset.chapterFrame) === i);
        if (frame) frame.style.opacity = amount;
      });
    }
  }
  customElements.define('product-story', ProductStory);
  window.ProductStoryCamera = { makeTimeline, poseAt, place };
})();
